/**
 * Oil & Gas Industry Configuration
 *
 * Comprehensive configuration for upstream, midstream, and downstream operations
 * Covers: Compressors, Pumps, Turbines, Heat Exchangers, Valves, Separators,
 * Wellheads, Pipelines, Tanks, Flares, PSVs, and Process Equipment
 *
 * Standards: API 618, API 610, API 670, API 570, API 653, ASME, NACE, EPA
 */

import { IndustryConfig } from '../industryTypes'

export const oilGasConfig: IndustryConfig = {
  id: 'oil_gas',
  name: 'Oil & Gas',
  description: 'Upstream exploration and production, midstream transportation, and downstream refining operations',
  icon: 'Droplets',

  // =============================================================================
  // TERMINOLOGY
  // =============================================================================
  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment',
    assetType: 'Equipment Type',
    failure: 'Failure',
    health: 'Health',
    healthScore: 'Health Score',
    healthy: 'Running',
    degraded: 'Degraded',
    critical: 'Shutdown',
    alert: 'Alert',
    process: 'Operation',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Downtime',
    workOrder: 'Work Order',
    prediction: 'Prediction',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Facility',
    facilityPlural: 'Facilities',
    location: 'Unit',
    operator: 'Operator',
    supervisor: 'Supervisor',
    calibration: 'Calibration',
    compliance: 'Compliance',
    criticality: 'Criticality',
  },

  // =============================================================================
  // ASSET TYPES (15 comprehensive types)
  // =============================================================================
  assetTypes: [
    {
      id: 'compressor',
      name: 'Compressor',
      icon: 'Gauge',
      description: 'Reciprocating and centrifugal compressors for gas compression',
      category: 'Rotating Equipment',
      defaultMetrics: ['discharge_pressure', 'discharge_temperature', 'vibration', 'efficiency', 'valve_temperature', 'rod_load'],
      maintenanceTypes: ['valve_service', 'piston_rod_inspection', 'bearing_replacement', 'ring_replacement', 'major_overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Production-critical, no redundancy', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major throughput impact', downtimeCostMultiplier: 25 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Partial redundancy available', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Full redundancy', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'pump',
      name: 'Pump',
      icon: 'Waves',
      description: 'Centrifugal, positive displacement, and submersible pumps',
      category: 'Rotating Equipment',
      defaultMetrics: ['flow_rate', 'head_pressure', 'vibration', 'seal_leakage', 'motor_current', 'bearing_temperature'],
      maintenanceTypes: ['seal_replacement', 'impeller_inspection', 'bearing_service', 'alignment', 'performance_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'No backup available', downtimeCostMultiplier: 40 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Limited backup capacity', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup available', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical service', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'turbine',
      name: 'Gas Turbine',
      icon: 'Fan',
      description: 'Gas turbines for power generation and mechanical drive',
      category: 'Prime Movers',
      defaultMetrics: ['exhaust_temperature', 'power_output', 'heat_rate', 'vibration', 'compressor_efficiency', 'inlet_dp'],
      maintenanceTypes: ['hot_gas_path_inspection', 'combustor_service', 'compressor_wash', 'bearing_inspection', 'major_inspection'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Single driver, no spare', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Limited spare capacity', downtimeCostMultiplier: 50 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Spare driver available', downtimeCostMultiplier: 20 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical application', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'heat_exchanger',
      name: 'Heat Exchanger',
      icon: 'Thermometer',
      description: 'Shell & tube, plate, and air-cooled heat exchangers',
      category: 'Static Equipment',
      defaultMetrics: ['approach_temperature', 'pressure_drop', 'fouling_factor', 'heat_transfer_coefficient', 'tube_wall_temp'],
      maintenanceTypes: ['tube_cleaning', 'tube_inspection', 'gasket_replacement', 'hydro_test', 'bundle_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Process critical', downtimeCostMultiplier: 30 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major process impact', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Moderate impact', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Utility service', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'separator',
      name: 'Separator',
      icon: 'SplitSquareVertical',
      description: 'Two-phase, three-phase, and test separators',
      category: 'Process Vessels',
      defaultMetrics: ['level', 'pressure', 'temperature', 'interface_level', 'retention_time', 'carryover'],
      maintenanceTypes: ['internal_inspection', 'instrumentation_calibration', 'relief_valve_test', 'thickness_measurement', 'weir_adjustment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Production separator', downtimeCostMultiplier: 40 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Test separator', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary separator', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Utility separator', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'control_valve',
      name: 'Control Valve',
      icon: 'GitBranch',
      description: 'Process control valves with pneumatic/electric actuators',
      category: 'Control Equipment',
      defaultMetrics: ['valve_position', 'travel', 'actuator_pressure', 'seat_leakage', 'response_time', 'hysteresis'],
      maintenanceTypes: ['packing_replacement', 'seat_lapping', 'actuator_service', 'positioner_calibration', 'trim_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Safety critical (SIS)', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Process critical', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Standard control', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'wellhead',
      name: 'Wellhead',
      icon: 'ArrowUpFromLine',
      description: 'Christmas tree and wellhead assembly for well control',
      category: 'Wellsite Equipment',
      defaultMetrics: ['tubing_pressure', 'casing_pressure', 'annulus_pressure', 'choke_size', 'flow_rate', 'temperature'],
      maintenanceTypes: ['valve_greasing', 'pressure_test', 'choke_service', 'tree_inspection', 'hanger_inspection'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Flowing well, no backup', downtimeCostMultiplier: 60 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'High producer', downtimeCostMultiplier: 30 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Average producer', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Low producer/stripper', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'psv',
      name: 'Pressure Safety Valve',
      icon: 'ShieldAlert',
      description: 'Pressure relief devices for overpressure protection',
      category: 'Safety Equipment',
      defaultMetrics: ['set_pressure', 'pop_pressure', 'seat_leakage', 'lift', 'blowdown'],
      maintenanceTypes: ['psv_testing', 'seat_repair', 'spring_replacement', 'full_overhaul', 'certification'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary overpressure protection', downtimeCostMultiplier: 0 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary protection', downtimeCostMultiplier: 0 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup relief', downtimeCostMultiplier: 0 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical service', downtimeCostMultiplier: 0 },
      ],
    },
    {
      id: 'pipeline',
      name: 'Pipeline',
      icon: 'ArrowRightLeft',
      description: 'Gathering, transmission, and distribution pipelines',
      category: 'Linear Assets',
      defaultMetrics: ['pressure', 'flow_rate', 'temperature', 'wall_thickness', 'cathodic_protection', 'pig_passage'],
      maintenanceTypes: ['pigging', 'inline_inspection', 'cp_survey', 'coating_repair', 'leak_repair', 'hydro_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main transmission line', downtimeCostMultiplier: 80 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major gathering line', downtimeCostMultiplier: 40 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary line', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Low pressure service', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'storage_tank',
      name: 'Storage Tank',
      icon: 'Container',
      description: 'Atmospheric and pressurized storage tanks',
      category: 'Storage Equipment',
      defaultMetrics: ['level', 'temperature', 'vapor_pressure', 'bottom_sediment', 'shell_thickness', 'settlement'],
      maintenanceTypes: ['tank_cleaning', 'api_653_inspection', 'floor_repair', 'roof_repair', 'coating_application', 'cathodic_protection'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Single tank, no redundancy', downtimeCostMultiplier: 30 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Limited redundancy', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Multiple tanks available', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Utility storage', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'flare',
      name: 'Flare System',
      icon: 'Flame',
      description: 'Flare header, knockout drum, and flare tip assembly',
      category: 'Safety Equipment',
      defaultMetrics: ['pilot_status', 'flame_temperature', 'header_pressure', 'knockout_level', 'combustion_efficiency'],
      maintenanceTypes: ['tip_inspection', 'pilot_service', 'knockout_cleaning', 'molecular_seal_check', 'ignition_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary flare', downtimeCostMultiplier: 0 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary flare', downtimeCostMultiplier: 0 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Ground flare', downtimeCostMultiplier: 0 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup system', downtimeCostMultiplier: 0 },
      ],
    },
    {
      id: 'dehydration_unit',
      name: 'Dehydration Unit',
      icon: 'Droplet',
      description: 'Glycol dehydration and molecular sieve units',
      category: 'Process Equipment',
      defaultMetrics: ['water_content', 'glycol_concentration', 'reboiler_temperature', 'contactor_dp', 'circulation_rate'],
      maintenanceTypes: ['glycol_filtration', 'reboiler_inspection', 'packing_replacement', 'still_column_service', 'glycol_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Pipeline spec required', downtimeCostMultiplier: 35 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Sales gas treatment', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Fuel gas treatment', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'amine_unit',
      name: 'Amine Unit',
      icon: 'FlaskConical',
      description: 'Amine sweetening unit for H2S and CO2 removal',
      category: 'Process Equipment',
      defaultMetrics: ['h2s_outlet', 'co2_outlet', 'amine_concentration', 'reboiler_duty', 'circulation_rate', 'solution_loading'],
      maintenanceTypes: ['amine_filtration', 'reclaimer_operation', 'tower_inspection', 'reboiler_service', 'amine_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'H2S removal critical', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Spec gas production', downtimeCostMultiplier: 30 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Partial sweetening', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Low H2S service', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'motor',
      name: 'Electric Motor',
      icon: 'Zap',
      description: 'Induction and synchronous motors for equipment drive',
      category: 'Rotating Equipment',
      defaultMetrics: ['current', 'power', 'winding_temperature', 'bearing_temperature', 'vibration', 'power_factor'],
      maintenanceTypes: ['insulation_test', 'bearing_service', 'winding_cleaning', 'alignment', 'motor_rewind'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Critical equipment driver', downtimeCostMultiplier: 40 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major equipment driver', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Spare available', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'instrumentation',
      name: 'Instrumentation',
      icon: 'Gauge',
      description: 'Transmitters, analyzers, and control systems',
      category: 'Control Equipment',
      defaultMetrics: ['signal_quality', 'calibration_error', 'response_time', 'drift', 'loop_accuracy'],
      maintenanceTypes: ['calibration', 'loop_check', 'analyzer_service', 'valve_positioner_cal', 'transmitter_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Safety critical (SIS/SIL)', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Process critical', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Monitoring only', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  // =============================================================================
  // KPI DOMAINS (8 comprehensive domains)
  // =============================================================================
  kpiDomains: [
    {
      id: 'production_performance',
      name: 'Production Performance',
      icon: 'TrendingUp',
      description: 'Production rates, efficiency, and throughput metrics',
      businessValue: 'Maximize production while minimizing costs',
      category: 'universal',
      metrics: [
        { id: 'production_rate', name: 'Production Rate', unit: 'boe/d', description: 'Barrels of oil equivalent per day', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 1000, warning: 800, critical: 500 } },
        { id: 'production_efficiency', name: 'Production Efficiency', unit: '%', description: 'Actual vs potential production', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'uptime', name: 'Uptime', unit: '%', description: 'Equipment operational availability', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'water_cut', name: 'Water Cut', unit: '%', description: 'Water percentage in production', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 20, warning: 50, critical: 80 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 85, max: 95 }, critical: { max: 85 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'efficiency_analysis',
      name: 'Equipment Efficiency',
      icon: 'Gauge',
      description: 'Operational efficiency and energy performance of rotating equipment',
      businessValue: 'Reduce energy costs and optimize equipment utilization',
      category: 'universal',
      metrics: [
        { id: 'compression_efficiency', name: 'Compression Efficiency', unit: '%', description: 'Isentropic/polytropic efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 75, critical: 65 } },
        { id: 'pump_efficiency', name: 'Pump Efficiency', unit: '%', description: 'Hydraulic efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 80, warning: 70, critical: 60 } },
        { id: 'fuel_consumption', name: 'Fuel Consumption', unit: 'MSCFD', description: 'Fuel gas usage', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 100, warning: 120, critical: 150 } },
        { id: 'heat_rate', name: 'Heat Rate', unit: 'BTU/hp-hr', description: 'Turbine thermal efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 8500, warning: 9500, critical: 11000 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'gas_composition',
      name: 'Gas Composition Analysis',
      icon: 'FlaskConical',
      description: 'Gas stream composition and quality monitoring',
      businessValue: 'Ensure product quality and pipeline specifications',
      category: 'industry_specific',
      metrics: [
        { id: 'methane_content', name: 'Methane Content', unit: '%', description: 'CH4 percentage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 85 } },
        { id: 'co2_content', name: 'CO2 Content', unit: '%', description: 'Carbon dioxide percentage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 4, critical: 6 } },
        { id: 'h2s_content', name: 'H2S Content', unit: 'ppm', description: 'Hydrogen sulfide level', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 4, warning: 10, critical: 20 } },
        { id: 'moisture', name: 'Moisture Content', unit: 'lb/mmscf', description: 'Water vapor content', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 4, warning: 7, critical: 10 } },
        { id: 'btu_value', name: 'BTU Value', unit: 'BTU/scf', description: 'Heating value', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 1050, warning: 1000, critical: 950 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['bar_chart', 'trend', 'radar', 'comparison'],
    },
    {
      id: 'condition_monitoring',
      name: 'Condition Monitoring',
      icon: 'Activity',
      description: 'Real-time equipment condition via sensors and analyzers',
      businessValue: 'Early detection of equipment degradation and wear',
      category: 'universal',
      metrics: [
        { id: 'vibration', name: 'Vibration', unit: 'mm/s', description: 'Vibration velocity (RMS)', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 4.5, warning: 7.1, critical: 11.2 } },
        { id: 'bearing_temperature', name: 'Bearing Temperature', unit: '°C', description: 'Bearing operating temperature', dataType: 'continuous', aggregation: 'max', goodDirection: 'target', defaultThresholds: { healthy: 70, warning: 85, critical: 100 } },
        { id: 'oil_pressure', name: 'Lube Oil Pressure', unit: 'psi', description: 'Lubrication system pressure', dataType: 'continuous', aggregation: 'min', goodDirection: 'target', defaultThresholds: { healthy: 45, warning: 35, critical: 25 } },
        { id: 'discharge_temp', name: 'Discharge Temperature', unit: '°C', description: 'Gas discharge temperature', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 135, warning: 150, critical: 165 } },
        { id: 'axial_position', name: 'Axial Position', unit: 'mil', description: 'Shaft axial displacement', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 15 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['line_chart', 'heatmap', 'distribution', 'gauge'],
    },
    {
      id: 'asset_integrity',
      name: 'Asset Integrity',
      icon: 'Shield',
      description: 'Corrosion, wall thickness, and structural integrity monitoring',
      businessValue: 'Prevent leaks, ensure safe operation, and extend asset life',
      category: 'industry_specific',
      metrics: [
        { id: 'wall_thickness', name: 'Wall Thickness', unit: 'mm', description: 'Remaining wall thickness', dataType: 'continuous', aggregation: 'min', goodDirection: 'higher', defaultThresholds: { healthy: 8, warning: 6, critical: 4 } },
        { id: 'corrosion_rate', name: 'Corrosion Rate', unit: 'mpy', description: 'Mils per year material loss', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
        { id: 'cp_potential', name: 'CP Potential', unit: 'mV', description: 'Cathodic protection potential (vs Cu/CuSO4)', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: -950, warning: -850, critical: -750 } },
        { id: 'leak_rate', name: 'Leak Detection', unit: 'ppm', description: 'Fugitive emissions level', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 100, warning: 500, critical: 1000 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['heatmap', 'trend', 'bar_chart', 'distribution'],
    },
    {
      id: 'safety_performance',
      name: 'Safety Performance',
      icon: 'ShieldAlert',
      description: 'Process safety and HSE performance indicators',
      businessValue: 'Ensure safe operations and regulatory compliance',
      category: 'industry_specific',
      metrics: [
        { id: 'psv_reliability', name: 'PSV Reliability', unit: '%', description: 'Pressure safety valve test pass rate', dataType: 'discrete', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'sif_availability', name: 'SIF Availability', unit: '%', description: 'Safety instrumented function availability', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.9, warning: 99, critical: 98 } },
        { id: 'ldar_compliance', name: 'LDAR Compliance', unit: '%', description: 'Leak detection and repair compliance', dataType: 'discrete', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'overdue_inspections', name: 'Overdue Inspections', unit: 'count', description: 'Number of overdue safety inspections', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 3, critical: 10 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['gauge', 'bar_chart', 'comparison', 'trend'],
    },
    {
      id: 'environmental_impact',
      name: 'Environmental Impact',
      icon: 'Leaf',
      description: 'Emissions and environmental compliance metrics',
      businessValue: 'Ensure regulatory compliance and minimize environmental footprint',
      category: 'industry_specific',
      metrics: [
        { id: 'methane_emissions', name: 'Methane Emissions', unit: 'tonnes/yr', description: 'Fugitive methane emissions', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 50, warning: 100, critical: 200 } },
        { id: 'flare_volume', name: 'Flare Volume', unit: 'MSCFD', description: 'Gas volume flared', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 50, critical: 100 } },
        { id: 'nox_emissions', name: 'NOx Emissions', unit: 'ppm', description: 'Nitrogen oxide in exhaust', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 25, warning: 50, critical: 100 } },
        { id: 'voc_emissions', name: 'VOC Emissions', unit: 'tonnes/yr', description: 'Volatile organic compounds', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 25, critical: 50 } },
      ],
      thresholds: { healthy: { max: 50 }, warning: { min: 50, max: 100 }, critical: { min: 100 } },
      visualizations: ['line_chart', 'bar_chart', 'trend', 'comparison'],
    },
    {
      id: 'reliability_metrics',
      name: 'Reliability Metrics',
      icon: 'TrendingUp',
      description: 'Equipment reliability and maintenance performance',
      businessValue: 'Optimize maintenance and maximize equipment availability',
      category: 'universal',
      metrics: [
        { id: 'mtbf', name: 'MTBF', unit: 'hours', description: 'Mean time between failures', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 8760, warning: 4380, critical: 2000 } },
        { id: 'mttr', name: 'MTTR', unit: 'hours', description: 'Mean time to repair', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 4, warning: 8, critical: 24 } },
        { id: 'pm_compliance', name: 'PM Compliance', unit: '%', description: 'Preventive maintenance completion rate', dataType: 'discrete', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'backlog_hours', name: 'Maintenance Backlog', unit: 'hours', description: 'Hours of deferred maintenance', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 100, warning: 500, critical: 1000 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['gauge', 'trend', 'comparison', 'bar_chart'],
    },
  ],

  // =============================================================================
  // MAINTENANCE TYPES (12 comprehensive types)
  // =============================================================================
  maintenanceTypes: [
    {
      id: 'valve_service',
      name: 'Compressor Valve Service',
      description: 'Inspection, lapping, and replacement of reciprocating compressor valves',
      category: 'preventive',
      defaultInterval: { value: 4000, unit: 'hours' },
      laborRequirements: [
        { role: 'Compressor Technician', count: 2, hourlyRate: 65 },
        { role: 'Helper', count: 1, hourlyRate: 35 },
      ],
      estimatedDuration: { min: 4, typical: 8, max: 16, unit: 'hours' },
      skillsRequired: ['compressor_mechanics', 'valve_lapping', 'torque_procedures'],
      partsCommonly: ['valve_plates', 'valve_springs', 'valve_gaskets', 'o_rings'],
    },
    {
      id: 'bearing_replacement',
      name: 'Bearing Replacement',
      description: 'Replacement of main bearings, rod bearings, or thrust bearings',
      category: 'predictive',
      laborRequirements: [
        { role: 'Compressor Technician', count: 2, hourlyRate: 65 },
        { role: 'Rotating Equipment Engineer', count: 1, hourlyRate: 95 },
        { role: 'Helper', count: 1, hourlyRate: 35 },
      ],
      estimatedDuration: { min: 16, typical: 24, max: 48, unit: 'hours' },
      skillsRequired: ['bearing_fitting', 'precision_alignment', 'torque_procedures', 'clearance_measurement'],
      partsCommonly: ['main_bearings', 'rod_bearings', 'thrust_bearings', 'bearing_shells'],
    },
    {
      id: 'seal_replacement',
      name: 'Mechanical Seal Replacement',
      description: 'Replacement of mechanical seals on pumps and compressors',
      category: 'predictive',
      laborRequirements: [
        { role: 'Pump Technician', count: 2, hourlyRate: 60 },
        { role: 'Rotating Equipment Engineer', count: 1, hourlyRate: 95 },
      ],
      estimatedDuration: { min: 4, typical: 8, max: 16, unit: 'hours' },
      skillsRequired: ['seal_installation', 'shaft_alignment', 'flush_system_setup'],
      partsCommonly: ['mechanical_seals', 'o_rings', 'gaskets', 'seal_flush_components'],
    },
    {
      id: 'major_overhaul',
      name: 'Major Overhaul',
      description: 'Complete equipment overhaul including all rotating and static components',
      category: 'preventive',
      defaultInterval: { value: 48000, unit: 'hours' },
      laborRequirements: [
        { role: 'Compressor Technician', count: 4, hourlyRate: 65 },
        { role: 'Rotating Equipment Engineer', count: 1, hourlyRate: 95 },
        { role: 'Supervisor', count: 1, hourlyRate: 85 },
        { role: 'Helper', count: 2, hourlyRate: 35 },
      ],
      estimatedDuration: { min: 5, typical: 7, max: 14, unit: 'days' },
      skillsRequired: ['compressor_overhaul', 'ndt_inspection', 'alignment', 'hydro_test'],
      partsCommonly: ['piston_rings', 'rider_bands', 'packing_rings', 'gaskets', 'bearings', 'valve_sets'],
    },
    {
      id: 'psv_testing',
      name: 'PSV Testing & Certification',
      description: 'Pressure safety valve testing, repair, and recertification per API 510/ASME',
      category: 'preventive',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'PSV Technician', count: 2, hourlyRate: 70 },
        { role: 'Inspector', count: 1, hourlyRate: 85 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['psv_testing', 'seat_lapping', 'spring_adjustment', 'api_510'],
      partsCommonly: ['valve_seats', 'valve_discs', 'springs', 'gaskets', 'bellows'],
    },
    {
      id: 'pipeline_pigging',
      name: 'Pipeline Pigging',
      description: 'Cleaning and inspection pigging operations',
      category: 'preventive',
      defaultInterval: { value: 6, unit: 'months' },
      laborRequirements: [
        { role: 'Pipeline Technician', count: 3, hourlyRate: 55 },
        { role: 'Supervisor', count: 1, hourlyRate: 85 },
      ],
      estimatedDuration: { min: 8, typical: 16, max: 48, unit: 'hours' },
      skillsRequired: ['pig_launching', 'pig_receiving', 'pipeline_operations'],
      partsCommonly: ['cleaning_pigs', 'gauge_pigs', 'smart_pig_cups', 'launcher_seals'],
    },
    {
      id: 'inline_inspection',
      name: 'Inline Inspection (ILI)',
      description: 'Smart pig inspection for wall thickness, dents, and anomalies',
      category: 'predictive',
      defaultInterval: { value: 5, unit: 'years' },
      laborRequirements: [
        { role: 'ILI Specialist', count: 2, hourlyRate: 125 },
        { role: 'Pipeline Technician', count: 2, hourlyRate: 55 },
        { role: 'Data Analyst', count: 1, hourlyRate: 90 },
      ],
      estimatedDuration: { min: 3, typical: 7, max: 14, unit: 'days' },
      skillsRequired: ['smart_pig_operations', 'data_analysis', 'integrity_assessment'],
      partsCommonly: ['smart_pig_batteries', 'sensor_modules', 'cups_and_discs'],
    },
    {
      id: 'turbine_hgp',
      name: 'Hot Gas Path Inspection',
      description: 'Combustion and hot gas path inspection for gas turbines',
      category: 'preventive',
      defaultInterval: { value: 12000, unit: 'hours' },
      laborRequirements: [
        { role: 'Turbine Specialist', count: 4, hourlyRate: 110 },
        { role: 'OEM Field Engineer', count: 1, hourlyRate: 175 },
        { role: 'Supervisor', count: 1, hourlyRate: 85 },
      ],
      estimatedDuration: { min: 10, typical: 14, max: 21, unit: 'days' },
      skillsRequired: ['turbine_disassembly', 'blade_inspection', 'ndt', 'combustor_service'],
      partsCommonly: ['combustion_liners', 'transition_pieces', 'first_stage_nozzles', 'fuel_nozzles'],
    },
    {
      id: 'tank_inspection',
      name: 'API 653 Tank Inspection',
      description: 'Comprehensive storage tank inspection per API 653',
      category: 'preventive',
      defaultInterval: { value: 10, unit: 'years' },
      laborRequirements: [
        { role: 'API 653 Inspector', count: 2, hourlyRate: 95 },
        { role: 'Tank Technician', count: 3, hourlyRate: 50 },
        { role: 'NDT Technician', count: 1, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 5, typical: 10, max: 21, unit: 'days' },
      skillsRequired: ['tank_inspection', 'ut_thickness', 'mfl_scanning', 'settlement_survey'],
      partsCommonly: ['floor_plates', 'shell_patches', 'roof_repairs', 'coating_materials'],
    },
    {
      id: 'compressor_wash',
      name: 'Compressor Water Wash',
      description: 'Online and offline water wash of turbine/compressor sections',
      category: 'condition_based',
      laborRequirements: [
        { role: 'Turbine Technician', count: 2, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['wash_procedures', 'water_quality', 'turbine_operations'],
      partsCommonly: ['wash_nozzles', 'filters', 'detergent'],
    },
    {
      id: 'glycol_service',
      name: 'Glycol System Service',
      description: 'Glycol dehydration unit maintenance and glycol replacement',
      category: 'preventive',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Process Technician', count: 2, hourlyRate: 55 },
        { role: 'Instrument Technician', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 8, typical: 16, max: 24, unit: 'hours' },
      skillsRequired: ['glycol_systems', 'reboiler_operation', 'contactor_maintenance'],
      partsCommonly: ['glycol', 'filters', 'packing', 'reboiler_tubes'],
    },
    {
      id: 'amine_service',
      name: 'Amine System Service',
      description: 'Amine sweetening unit maintenance and amine reclamation',
      category: 'preventive',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Process Technician', count: 2, hourlyRate: 55 },
        { role: 'Chemical Engineer', count: 1, hourlyRate: 95 },
      ],
      estimatedDuration: { min: 16, typical: 24, max: 48, unit: 'hours' },
      skillsRequired: ['amine_systems', 'reclaimer_operation', 'tower_maintenance'],
      partsCommonly: ['amine_solution', 'filters', 'packing', 'heat_exchanger_gaskets'],
    },
  ],

  // =============================================================================
  // RISK FACTORS
  // =============================================================================
  riskFactors: [
    { id: 'production_loss', name: 'Production Loss Risk', description: 'Risk of production interruption or deferral', category: 'operational', weight: 0.30, calculationMethod: 'exponential', inputs: ['health_score', 'redundancy_factor', 'criticality'] },
    { id: 'safety_risk', name: 'Process Safety Risk', description: 'Risk of process safety incidents (Tier 1/2)', category: 'safety', weight: 0.35, calculationMethod: 'threshold', inputs: ['psi_kpi', 'psv_status', 'sif_availability', 'leak_detection'] },
    { id: 'environmental_risk', name: 'Environmental Risk', description: 'Risk of environmental impact or permit exceedance', category: 'environmental', weight: 0.20, calculationMethod: 'linear', inputs: ['emissions_trend', 'leak_rate', 'flare_volume'] },
    { id: 'regulatory_risk', name: 'Regulatory Risk', description: 'Risk of regulatory non-compliance', category: 'compliance', weight: 0.10, calculationMethod: 'threshold', inputs: ['permit_status', 'inspection_overdue', 'certification_status'] },
    { id: 'financial_risk', name: 'Financial Risk', description: 'Risk of unplanned maintenance costs', category: 'financial', weight: 0.05, calculationMethod: 'linear', inputs: ['equipment_age', 'failure_history', 'spare_parts_availability'] },
  ],

  // =============================================================================
  // COMPLIANCE STANDARDS (10 comprehensive standards)
  // =============================================================================
  complianceStandards: [
    {
      id: 'api_618',
      name: 'API 618 Reciprocating Compressors',
      shortName: 'API 618',
      description: 'Reciprocating compressor design, manufacture, and testing requirements',
      authority: 'American Petroleum Institute',
      region: ['Global'],
      requirements: [
        { id: 'vibration_limit', name: 'Vibration Limits', description: 'Maximum allowable unfiltered vibration', kpiId: 'vibration', threshold: 7.1, operator: 'lt', severity: 'required' },
        { id: 'temp_limit', name: 'Discharge Temperature', description: 'Maximum discharge temperature', kpiId: 'discharge_temp', threshold: 165, operator: 'lt', severity: 'critical' },
        { id: 'rod_load', name: 'Rod Load', description: 'Maximum rod load percentage', kpiId: 'rod_load_pct', threshold: 100, operator: 'lte', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'api_610',
      name: 'API 610 Centrifugal Pumps',
      shortName: 'API 610',
      description: 'Centrifugal pump design and construction for petroleum, petrochemical, and natural gas',
      authority: 'American Petroleum Institute',
      region: ['Global'],
      requirements: [
        { id: 'vibration', name: 'Vibration Velocity', description: 'Maximum vibration velocity', kpiId: 'vibration', threshold: 5.0, operator: 'lt', severity: 'required' },
        { id: 'npsh', name: 'NPSH Margin', description: 'Minimum NPSH margin', kpiId: 'npsh_margin', threshold: 1.0, operator: 'gte', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'api_670',
      name: 'API 670 Machinery Protection',
      shortName: 'API 670',
      description: 'Machinery protection systems for vibration, axial position, and temperature',
      authority: 'American Petroleum Institute',
      region: ['Global'],
      requirements: [
        { id: 'radial_vib', name: 'Radial Vibration Alert', description: 'Radial vibration alert setpoint', kpiId: 'radial_vibration', threshold: 5.1, operator: 'lt', severity: 'advisory' },
        { id: 'radial_vib_danger', name: 'Radial Vibration Danger', description: 'Radial vibration shutdown setpoint', kpiId: 'radial_vibration', threshold: 7.6, operator: 'lt', severity: 'critical' },
        { id: 'axial', name: 'Axial Position', description: 'Axial position alarm', kpiId: 'axial_position', threshold: 10, operator: 'lt', severity: 'required' },
      ],
      reportingFrequency: 'continuous',
    },
    {
      id: 'api_570',
      name: 'API 570 Piping Inspection',
      shortName: 'API 570',
      description: 'In-service inspection, rating, repair, and alteration of piping systems',
      authority: 'American Petroleum Institute',
      region: ['Global'],
      requirements: [
        { id: 'wall_thickness', name: 'Minimum Wall Thickness', description: 'Remaining wall above minimum required', kpiId: 'wall_thickness', threshold: 0, operator: 'gt', severity: 'critical' },
        { id: 'inspection_interval', name: 'Inspection Interval', description: 'Maximum inspection interval', kpiId: 'inspection_age', threshold: 5, operator: 'lte', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'api_653',
      name: 'API 653 Tank Inspection',
      shortName: 'API 653',
      description: 'Tank inspection, repair, alteration, and reconstruction',
      authority: 'American Petroleum Institute',
      region: ['Global'],
      requirements: [
        { id: 'external_interval', name: 'External Inspection', description: 'Maximum external inspection interval', kpiId: 'external_inspection_age', threshold: 5, operator: 'lte', severity: 'required' },
        { id: 'internal_interval', name: 'Internal Inspection', description: 'Maximum internal inspection interval', kpiId: 'internal_inspection_age', threshold: 10, operator: 'lte', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'epa_nsps',
      name: 'EPA NSPS Subpart OOOO/OOOOa',
      shortName: 'EPA NSPS',
      description: 'New Source Performance Standards for methane and VOC emissions',
      authority: 'Environmental Protection Agency',
      region: ['US'],
      requirements: [
        { id: 'ldar', name: 'LDAR Program', description: 'Leak detection and repair compliance', kpiId: 'ldar_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'flare_efficiency', name: 'Flare Efficiency', description: 'Minimum flare destruction efficiency', kpiId: 'flare_efficiency', threshold: 98, operator: 'gte', severity: 'required' },
        { id: 'pneumatic', name: 'Pneumatic Controllers', description: 'Low-bleed controller requirement', kpiId: 'pneumatic_compliance', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'phmsa_192',
      name: 'PHMSA 49 CFR 192',
      shortName: 'PHMSA 192',
      description: 'Transportation of natural and other gas by pipeline safety standards',
      authority: 'Pipeline and Hazardous Materials Safety Administration',
      region: ['US'],
      requirements: [
        { id: 'maop', name: 'MAOP Compliance', description: 'Maximum allowable operating pressure', kpiId: 'pressure_pct_maop', threshold: 100, operator: 'lte', severity: 'critical' },
        { id: 'patrol', name: 'Patrol Frequency', description: 'Right-of-way patrol frequency', kpiId: 'patrol_age', threshold: 14, operator: 'lte', severity: 'required' },
        { id: 'valve', name: 'Valve Maintenance', description: 'Valve inspection and maintenance', kpiId: 'valve_inspection_age', threshold: 12, operator: 'lte', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'asme_b31_3',
      name: 'ASME B31.3 Process Piping',
      shortName: 'ASME B31.3',
      description: 'Process piping design, construction, and testing requirements',
      authority: 'American Society of Mechanical Engineers',
      region: ['Global'],
      requirements: [
        { id: 'pressure_test', name: 'Pressure Test', description: 'Hydrostatic/pneumatic test requirements', kpiId: 'pressure_test_status', threshold: 1, operator: 'eq', severity: 'critical' },
        { id: 'ndt', name: 'NDT Requirements', description: 'Non-destructive testing requirements', kpiId: 'ndt_compliance', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'nace_sp0169',
      name: 'NACE SP0169 CP of Steel',
      shortName: 'NACE SP0169',
      description: 'Control of external corrosion on underground or submerged metallic piping systems',
      authority: 'NACE International',
      region: ['Global'],
      requirements: [
        { id: 'cp_potential', name: 'CP Potential', description: 'Minimum cathodic protection potential', kpiId: 'cp_potential', threshold: -850, operator: 'lte', severity: 'required' },
        { id: 'cp_survey', name: 'CP Survey Frequency', description: 'Annual CP survey requirement', kpiId: 'cp_survey_age', threshold: 12, operator: 'lte', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'iec_61511',
      name: 'IEC 61511 Functional Safety',
      shortName: 'IEC 61511',
      description: 'Functional safety - Safety instrumented systems for the process industry sector',
      authority: 'International Electrotechnical Commission',
      region: ['Global'],
      requirements: [
        { id: 'sif_demand', name: 'SIF Demand Rate', description: 'Safety instrumented function demand rate', kpiId: 'sif_demand_rate', threshold: 0.1, operator: 'lt', severity: 'advisory' },
        { id: 'proof_test', name: 'Proof Test Interval', description: 'Maximum proof test interval', kpiId: 'proof_test_age', threshold: 12, operator: 'lte', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  // =============================================================================
  // PROFESSIONAL ROLES (12 roles)
  // =============================================================================
  professionalRoles: [
    {
      id: 'operations_superintendent',
      category: 'executive' as const,
      name: 'Operations Superintendent',
      shortName: 'Ops Supt',
      description: 'Oversees all field operations and production optimization',
      responsibilities: ['Production optimization', 'Resource allocation', 'Safety leadership', 'Budget management'],
      focusAreas: ['Production rates', 'Operating costs', 'Safety metrics', 'Reliability'],
      explanationStyle: {
        depth: 'high_level' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive' as const,
        focusOn: ['business_impact', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Production changes', 'Budget approval', 'Contractor management'],
      reportingTo: 'Asset Manager',
      icon: 'Building',
    },
    {
      id: 'reliability_engineer',
      category: 'sme' as const,
      name: 'Reliability Engineer',
      shortName: 'Rel Eng',
      description: 'Ensures equipment reliability through analysis and optimization',
      responsibilities: ['Root cause analysis', 'Failure prevention', 'Reliability improvement', 'Maintenance optimization'],
      focusAreas: ['Equipment health', 'MTBF/MTTR', 'Failure patterns', 'Spare parts strategy'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Maintenance scheduling', 'Parts replacement approval', 'Vendor selection'],
      reportingTo: 'Operations Superintendent',
      icon: 'Settings',
    },
    {
      id: 'production_engineer',
      category: 'sme' as const,
      name: 'Production Engineer',
      shortName: 'Prod Eng',
      description: 'Optimizes well and facility performance for maximum production',
      responsibilities: ['Well optimization', 'Artificial lift design', 'Production allocation', 'Decline curve analysis'],
      focusAreas: ['Production rates', 'Well performance', 'Reservoir behavior', 'Lift optimization'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'business_impact', 'action_items'] as const,
      },
      decisionAuthority: ['Well workover', 'Lift system changes', 'Production testing'],
      reportingTo: 'Operations Superintendent',
      icon: 'TrendingUp',
    },
    {
      id: 'process_engineer',
      category: 'sme' as const,
      name: 'Process Engineer',
      shortName: 'Proc Eng',
      description: 'Optimizes facility processes and troubleshoots operational issues',
      responsibilities: ['Process optimization', 'Troubleshooting', 'Debottlenecking', 'Energy efficiency'],
      focusAreas: ['Process efficiency', 'Product quality', 'Energy consumption', 'Capacity utilization'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items', 'compliance'] as const,
      },
      decisionAuthority: ['Operating parameters', 'Chemical injection', 'Process modifications'],
      reportingTo: 'Operations Superintendent',
      icon: 'FlaskConical',
    },
    {
      id: 'hse_coordinator',
      category: 'sme' as const,
      name: 'HSE Coordinator',
      shortName: 'HSE',
      description: 'Ensures health, safety, and environmental compliance',
      responsibilities: ['Safety program management', 'Environmental compliance', 'Incident investigation', 'Training coordination'],
      focusAreas: ['Safety performance', 'Environmental metrics', 'Regulatory compliance', 'Risk management'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['risk', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Stop work authority', 'Permit approval', 'Emergency response'],
      reportingTo: 'Operations Superintendent',
      icon: 'ShieldAlert',
    },
    {
      id: 'maintenance_planner',
      category: 'technician' as const,
      name: 'Maintenance Planner',
      shortName: 'Planner',
      description: 'Plans and schedules all maintenance activities',
      responsibilities: ['Work order planning', 'Resource scheduling', 'Parts coordination', 'Backlog management'],
      focusAreas: ['Schedule adherence', 'Resource utilization', 'Parts availability', 'PM compliance'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['action_items', 'technical_details', 'business_impact'] as const,
      },
      decisionAuthority: ['Work scheduling', 'Resource allocation', 'Priority assignment'],
      reportingTo: 'Reliability Engineer',
      icon: 'Calendar',
    },
    {
      id: 'field_operator',
      category: 'technician' as const,
      name: 'Field Operator',
      shortName: 'Operator',
      description: 'Operates and monitors field equipment and wells',
      responsibilities: ['Equipment operation', 'Rounds and monitoring', 'Sample collection', 'Minor maintenance'],
      focusAreas: ['Equipment status', 'Process readings', 'Safety hazards', 'Anomalies'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['action_items', 'technical_details'] as const,
      },
      decisionAuthority: ['Equipment start/stop', 'Setpoint adjustment', 'Emergency shutdown'],
      reportingTo: 'Shift Supervisor',
      icon: 'User',
    },
    {
      id: 'compressor_technician',
      category: 'technician' as const,
      name: 'Compressor Technician',
      shortName: 'Comp Tech',
      description: 'Maintains and repairs reciprocating and centrifugal compressors',
      responsibilities: ['Valve service', 'Bearing replacement', 'Alignment', 'Troubleshooting'],
      focusAreas: ['Vibration', 'Temperature', 'Valve condition', 'Performance'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      },
      decisionAuthority: ['Repair method', 'Parts replacement', 'Alignment decisions'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Gauge',
    },
    {
      id: 'instrumentation_technician',
      category: 'technician' as const,
      name: 'Instrumentation Technician',
      shortName: 'I&E Tech',
      description: 'Maintains and calibrates instrumentation and control systems',
      responsibilities: ['Calibration', 'Loop troubleshooting', 'Transmitter maintenance', 'Control valve service'],
      focusAreas: ['Calibration accuracy', 'Loop performance', 'Safety systems', 'Control systems'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items', 'compliance'] as const,
      },
      decisionAuthority: ['Calibration decisions', 'Component replacement', 'Loop modifications'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Gauge',
    },
    {
      id: 'pipeline_technician',
      category: 'technician' as const,
      name: 'Pipeline Technician',
      shortName: 'Pipeline Tech',
      description: 'Maintains pipeline integrity and performs field repairs',
      responsibilities: ['CP surveys', 'Pigging operations', 'Leak repairs', 'ROW patrols'],
      focusAreas: ['Cathodic protection', 'Wall thickness', 'Coating condition', 'Encroachments'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items', 'compliance'] as const,
      },
      decisionAuthority: ['Repair method', 'CP rectifier settings', 'Pig selection'],
      reportingTo: 'Pipeline Supervisor',
      icon: 'ArrowRightLeft',
    },
    {
      id: 'rotating_equipment_engineer',
      category: 'sme' as const,
      name: 'Rotating Equipment Engineer',
      shortName: 'RE Eng',
      description: 'Specialist engineer for pumps, compressors, and turbines',
      responsibilities: ['Vibration analysis', 'Bearing analysis', 'Root cause analysis', 'Specification development'],
      focusAreas: ['Vibration signatures', 'Bearing health', 'Seal performance', 'Alignment'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Repair vs replace', 'Bearing selection', 'Seal design', 'Vendor approval'],
      reportingTo: 'Reliability Engineer',
      icon: 'Cog',
    },
    {
      id: 'integrity_engineer',
      category: 'sme' as const,
      name: 'Integrity Engineer',
      shortName: 'Int Eng',
      description: 'Manages asset integrity and inspection programs',
      responsibilities: ['RBI program', 'Corrosion management', 'Inspection planning', 'Fitness for service'],
      focusAreas: ['Wall thickness', 'Corrosion rates', 'Remaining life', 'Inspection results'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['risk', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Inspection intervals', 'Repair vs replace', 'Operating limits'],
      reportingTo: 'Reliability Engineer',
      icon: 'Shield',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (comprehensive)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'compressor_parts',
        name: 'Compressor Parts',
        description: 'Parts for reciprocating and centrifugal compressors',
        parts: [
          { id: 'VALVE-001', partNumber: 'VALVE-001', name: 'Compressor Valve Assembly', description: 'Complete valve assembly for reciprocating compressor', category: 'compressor_parts', manufacturer: 'Dresser-Rand', unitCost: 2500, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor'], failureModes: ['valve_leak', 'valve_break'] },
          { id: 'VALVE-002', partNumber: 'VALVE-002', name: 'Valve Plate', description: 'Stainless steel valve plate', category: 'compressor_parts', manufacturer: 'Cook', unitCost: 350, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['compressor'], failureModes: ['valve_wear'] },
          { id: 'VALVE-003', partNumber: 'VALVE-003', name: 'Valve Spring', description: 'Valve spring set', category: 'compressor_parts', manufacturer: 'Cook', unitCost: 85, currency: 'USD', leadTime: { value: 3, unit: 'days' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['compressor'], failureModes: ['spring_fatigue'] },
          { id: 'RING-001', partNumber: 'RING-001', name: 'Piston Ring Set', description: 'PTFE filled piston rings', category: 'compressor_parts', manufacturer: 'CompAir', unitCost: 1200, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor'], failureModes: ['ring_wear', 'blow_by'] },
          { id: 'RING-002', partNumber: 'RING-002', name: 'Rider Ring', description: 'Bronze filled rider ring', category: 'compressor_parts', manufacturer: 'CompAir', unitCost: 450, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['compressor'], failureModes: ['rider_wear'] },
          { id: 'PACK-001', partNumber: 'PACK-001', name: 'Packing Ring Set', description: 'Piston rod packing rings', category: 'compressor_parts', manufacturer: 'John Crane', unitCost: 1800, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor'], failureModes: ['packing_leak'] },
          { id: 'BRG-001', partNumber: 'BRG-001', name: 'Main Bearing', description: 'Babbitt lined main bearing', category: 'compressor_parts', manufacturer: 'Kingsbury', unitCost: 8500, currency: 'USD', leadTime: { value: 4, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor'], failureModes: ['bearing_wear', 'bearing_wipe'] },
          { id: 'BRG-002', partNumber: 'BRG-002', name: 'Crosshead Bearing', description: 'Crosshead pin bearing', category: 'compressor_parts', manufacturer: 'Kingsbury', unitCost: 3200, currency: 'USD', leadTime: { value: 3, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor'], failureModes: ['bearing_wear'] },
        ],
      },
      {
        id: 'seal_parts',
        name: 'Seals and Packing',
        description: 'Mechanical seals and packing for rotating equipment',
        parts: [
          { id: 'SEAL-001', partNumber: 'SEAL-001', name: 'Mechanical Seal', description: 'Cartridge mechanical seal for pumps', category: 'seal_parts', manufacturer: 'John Crane', unitCost: 3500, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['pump'], failureModes: ['seal_leak', 'seal_face_wear'] },
          { id: 'SEAL-002', partNumber: 'SEAL-002', name: 'Dry Gas Seal', description: 'Tandem dry gas seal for compressors', category: 'seal_parts', manufacturer: 'John Crane', unitCost: 45000, currency: 'USD', leadTime: { value: 8, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor'], failureModes: ['seal_leak', 'face_damage'] },
          { id: 'SEAL-003', partNumber: 'SEAL-003', name: 'Labyrinth Seal', description: 'Labyrinth seal for turbines', category: 'seal_parts', manufacturer: 'Flowserve', unitCost: 2800, currency: 'USD', leadTime: { value: 3, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['turbine'], failureModes: ['seal_wear'] },
        ],
      },
      {
        id: 'bearing_parts',
        name: 'Bearings',
        description: 'Rolling element and journal bearings',
        parts: [
          { id: 'BRG-010', partNumber: 'BRG-010', name: 'Thrust Bearing', description: 'Kingsbury thrust bearing', category: 'bearing_parts', manufacturer: 'Kingsbury', unitCost: 12000, currency: 'USD', leadTime: { value: 6, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['compressor', 'turbine'], failureModes: ['thrust_overload', 'bearing_wipe'] },
          { id: 'BRG-011', partNumber: 'BRG-011', name: 'Journal Bearing', description: 'Tilting pad journal bearing', category: 'bearing_parts', manufacturer: 'Waukesha', unitCost: 8500, currency: 'USD', leadTime: { value: 4, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['turbine', 'compressor'], failureModes: ['bearing_wear', 'oil_starvation'] },
          { id: 'BRG-012', partNumber: 'BRG-012', name: 'Ball Bearing', description: 'Angular contact ball bearing', category: 'bearing_parts', manufacturer: 'SKF', unitCost: 850, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['pump', 'motor'], failureModes: ['bearing_fatigue'] },
        ],
      },
      {
        id: 'valve_parts',
        name: 'Control Valve Parts',
        description: 'Parts for control valves and actuators',
        parts: [
          { id: 'CVP-001', partNumber: 'CVP-001', name: 'Valve Trim Kit', description: 'Complete trim kit with plug and seat', category: 'valve_parts', manufacturer: 'Fisher', unitCost: 4500, currency: 'USD', leadTime: { value: 4, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['control_valve'], failureModes: ['seat_erosion', 'plug_damage'] },
          { id: 'CVP-002', partNumber: 'CVP-002', name: 'Packing Set', description: 'Graphite packing set', category: 'valve_parts', manufacturer: 'Fisher', unitCost: 350, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['control_valve'], failureModes: ['packing_leak'] },
          { id: 'CVP-003', partNumber: 'CVP-003', name: 'Positioner', description: 'Digital valve positioner', category: 'valve_parts', manufacturer: 'Fisher', unitCost: 2800, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['control_valve'], failureModes: ['positioner_failure'] },
          { id: 'CVP-004', partNumber: 'CVP-004', name: 'Actuator Diaphragm', description: 'Pneumatic actuator diaphragm', category: 'valve_parts', manufacturer: 'Fisher', unitCost: 650, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['control_valve'], failureModes: ['diaphragm_rupture'] },
        ],
      },
      {
        id: 'psv_parts',
        name: 'Safety Valve Parts',
        description: 'Parts for pressure safety valves',
        parts: [
          { id: 'PSV-001', partNumber: 'PSV-001', name: 'PSV Repair Kit', description: 'Complete repair kit with seat, disc, and spring', category: 'psv_parts', manufacturer: 'Consolidated', unitCost: 1200, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['psv'], failureModes: ['set_point_drift', 'seat_leak'] },
          { id: 'PSV-002', partNumber: 'PSV-002', name: 'Spring Assembly', description: 'Range spring with adjustment screw', category: 'psv_parts', manufacturer: 'Consolidated', unitCost: 450, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['psv'], failureModes: ['spring_fatigue'] },
          { id: 'PSV-003', partNumber: 'PSV-003', name: 'Bellows Assembly', description: 'Balanced bellows for backpressure service', category: 'psv_parts', manufacturer: 'Consolidated', unitCost: 2800, currency: 'USD', leadTime: { value: 3, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['psv'], failureModes: ['bellows_rupture'] },
        ],
      },
      {
        id: 'turbine_parts',
        name: 'Gas Turbine Parts',
        description: 'Parts for gas turbines and accessories',
        parts: [
          { id: 'GTB-001', partNumber: 'GTB-001', name: 'Combustion Liner', description: 'Combustion chamber liner', category: 'turbine_parts', manufacturer: 'GE', unitCost: 85000, currency: 'USD', leadTime: { value: 16, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['turbine'], failureModes: ['liner_crack', 'hot_spot'] },
          { id: 'GTB-002', partNumber: 'GTB-002', name: 'Transition Piece', description: 'Combustor to turbine transition', category: 'turbine_parts', manufacturer: 'GE', unitCost: 65000, currency: 'USD', leadTime: { value: 16, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['turbine'], failureModes: ['crack', 'distortion'] },
          { id: 'GTB-003', partNumber: 'GTB-003', name: 'First Stage Nozzle', description: 'Hot gas path first stage nozzle', category: 'turbine_parts', manufacturer: 'GE', unitCost: 125000, currency: 'USD', leadTime: { value: 20, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['turbine'], failureModes: ['erosion', 'crack'] },
          { id: 'GTB-004', partNumber: 'GTB-004', name: 'Fuel Nozzle', description: 'DLN fuel nozzle assembly', category: 'turbine_parts', manufacturer: 'GE', unitCost: 28000, currency: 'USD', leadTime: { value: 8, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['turbine'], failureModes: ['plugging', 'wear'] },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE LIBRARY
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'mechanical',
        name: 'Mechanical Failures',
        description: 'Root causes related to mechanical wear and component degradation',
        icon: 'Settings',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'bearing_failure',
            name: 'Bearing Failure',
            description: 'Bearing degradation from wear, fatigue, or lubrication issues',
            category: 'mechanical',
            symptoms: ['Elevated vibration at bearing frequencies', 'High bearing temperature', 'Audible noise (grinding, squealing)', 'Metal particles in oil'],
            diagnosticSteps: [
              { order: 1, action: 'Perform vibration analysis focusing on bearing frequencies (BPFO, BPFI, BSF, FTF)', expectedResult: 'Identify bearing defect frequencies and severity', tools: ['Vibration analyzer', 'FFT software'], duration: 30, skillLevel: 'advanced' as const },
              { order: 2, action: 'Measure bearing temperature differential vs baseline', expectedResult: 'Temperature rise >15°C indicates problem', tools: ['IR thermometer', 'Thermal camera'], duration: 10, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Collect and analyze oil sample', expectedResult: 'Check for wear metals and contamination', tools: ['Oil sampling kit', 'Particle counter'], duration: 20, skillLevel: 'intermediate' as const },
            ],
            preventiveActions: ['Implement vibration-based condition monitoring', 'Maintain proper lubrication schedule', 'Use precision installation techniques', 'Monitor oil cleanliness'],
            typicalProbability: 0.20,
            affectedComponents: ['Bearings', 'Shafts', 'Housings', 'Seals'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 12, max: 48, unit: 'hours' as const },
            costRange: { min: 5000, max: 50000, currency: 'USD' },
            industryReferences: ['ISO 10816', 'API 670', 'SKF Bearing Handbook'],
          },
          {
            id: 'valve_failure',
            name: 'Compressor Valve Failure',
            description: 'Valve plate, spring, or seat degradation causing leakage or breakage',
            category: 'mechanical',
            symptoms: ['Elevated valve cap temperature', 'Reduced discharge pressure', 'Increased power consumption', 'Valve clicking or rattling'],
            diagnosticSteps: [
              { order: 1, action: 'Perform valve cap temperature survey', expectedResult: 'Identify hot valves indicating leakage', tools: ['IR thermometer', 'Thermal camera'], duration: 20, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Analyze PV card for valve events', expectedResult: 'Identify late closing, flutter, or leakage patterns', tools: ['PV analyzer', 'Pressure transducers'], duration: 45, skillLevel: 'advanced' as const },
              { order: 3, action: 'Remove and inspect valve assembly', expectedResult: 'Visual inspection of plates, springs, and seats', tools: ['Valve removal tools', 'Inspection gauge'], duration: 60, skillLevel: 'intermediate' as const, safetyPrecautions: ['Lockout/Tagout', 'Pressure isolation', 'Gas test'] },
            ],
            preventiveActions: ['Monitor valve temperatures regularly', 'Schedule valve service per run hours', 'Maintain gas filtration', 'Use correct valve design for application'],
            typicalProbability: 0.25,
            affectedComponents: ['Valve plates', 'Valve springs', 'Valve seats', 'Valve guards'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 2000, max: 25000, currency: 'USD' },
            industryReferences: ['API 618', 'Dresser-Rand Technical Bulletin'],
          },
          {
            id: 'seal_failure',
            name: 'Mechanical Seal Failure',
            description: 'Seal face wear or failure causing process leakage',
            category: 'mechanical',
            symptoms: ['Visible leakage at seal area', 'Elevated seal housing temperature', 'Low barrier fluid pressure', 'Environmental alarms'],
            diagnosticSteps: [
              { order: 1, action: 'Visual inspection for leakage', expectedResult: 'Identify leak location and severity', tools: ['Flashlight', 'Gas detector'], duration: 15, skillLevel: 'basic' as const, safetyPrecautions: ['Gas test required', 'Appropriate PPE'] },
              { order: 2, action: 'Check seal support system parameters', expectedResult: 'Verify barrier pressure, temperature, and flow', tools: ['Local gauges', 'Seal panel readings'], duration: 15, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Review operating conditions vs seal design', expectedResult: 'Confirm operation within seal envelope', tools: ['Process data', 'Seal specification'], duration: 30, skillLevel: 'advanced' as const },
            ],
            preventiveActions: ['Maintain seal support systems', 'Monitor seal parameters', 'Operate within design envelope', 'Use correct seal for application'],
            typicalProbability: 0.18,
            affectedComponents: ['Seal faces', 'O-rings', 'Springs', 'Drive mechanisms'],
            requiredExpertise: ['sme' as const, 'technician' as const],
            estimatedResolutionTime: { min: 4, typical: 12, max: 48, unit: 'hours' as const },
            costRange: { min: 5000, max: 50000, currency: 'USD' },
            industryReferences: ['API 682', 'API 685'],
          },
        ],
      },
      {
        id: 'process',
        name: 'Process Issues',
        description: 'Root causes related to process conditions and operations',
        icon: 'Activity',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'hydrate_formation',
            name: 'Hydrate Formation',
            description: 'Ice-like structures forming in gas processing systems',
            category: 'process',
            symptoms: ['Pressure drop increase', 'Flow restriction', 'Instrument freezing', 'Control valve sticking'],
            diagnosticSteps: [
              { order: 1, action: 'Review operating conditions vs hydrate curve', expectedResult: 'Confirm operation in hydrate formation zone', tools: ['Hydrate prediction software', 'Process data'], duration: 30, skillLevel: 'advanced' as const },
              { order: 2, action: 'Check methanol/glycol injection system', expectedResult: 'Verify adequate inhibitor injection', tools: ['Injection rate data', 'Tank levels'], duration: 20, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Apply heat or controlled depressurization', expectedResult: 'Hydrate dissociation and flow restoration', tools: ['Steam', 'Heat tracing', 'Process control'], duration: 120, skillLevel: 'advanced' as const, safetyPrecautions: ['Controlled depressurization', 'Monitor for gas release'] },
            ],
            preventiveActions: ['Maintain adequate inhibitor injection', 'Keep process above hydrate temperature', 'Proper dehydration upstream', 'Heat tracing on vulnerable points'],
            typicalProbability: 0.12,
            affectedComponents: ['Pipelines', 'Valves', 'Instruments', 'Heat exchangers'],
            requiredExpertise: ['sme' as const, 'executive' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 48, unit: 'hours' as const },
            costRange: { min: 1000, max: 100000, currency: 'USD' },
            industryReferences: ['API RP 17A', 'GPSA Engineering Data Book'],
          },
          {
            id: 'fouling',
            name: 'Heat Exchanger Fouling',
            description: 'Deposit buildup reducing heat transfer efficiency',
            category: 'process',
            symptoms: ['Increased approach temperature', 'Higher pressure drop', 'Reduced throughput', 'Elevated outlet temperature'],
            diagnosticSteps: [
              { order: 1, action: 'Calculate fouling resistance from operating data', expectedResult: 'Quantify fouling level vs design', tools: ['Process data', 'Design data sheet'], duration: 30, skillLevel: 'advanced' as const },
              { order: 2, action: 'Trend heat transfer coefficient over time', expectedResult: 'Identify fouling rate and pattern', tools: ['Historian data', 'Analysis software'], duration: 45, skillLevel: 'advanced' as const },
              { order: 3, action: 'Perform cleaning and inspect', expectedResult: 'Remove deposits and identify fouling type', tools: ['Cleaning equipment', 'Borescope'], duration: 480, skillLevel: 'intermediate' as const, safetyPrecautions: ['Lockout/Tagout', 'Confined space entry'] },
            ],
            preventiveActions: ['Monitor fouling indicators', 'Treat feed streams appropriately', 'Schedule cleaning based on condition', 'Consider anti-fouling coatings'],
            typicalProbability: 0.15,
            affectedComponents: ['Tubes', 'Shell', 'Baffles', 'Nozzles'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' as const },
            costRange: { min: 10000, max: 100000, currency: 'USD' },
            industryReferences: ['TEMA', 'HTRI Guidelines'],
          },
        ],
      },
      {
        id: 'corrosion',
        name: 'Corrosion Issues',
        description: 'Root causes related to material degradation from corrosive environments',
        icon: 'Flame',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'co2_corrosion',
            name: 'CO2 Corrosion (Sweet Corrosion)',
            description: 'Carbon dioxide corrosion in wet gas environments',
            category: 'corrosion',
            symptoms: ['Wall thickness loss', 'Pitting on internals', 'Increased iron in produced water', 'Leak development'],
            diagnosticSteps: [
              { order: 1, action: 'Review corrosion coupon data', expectedResult: 'Quantify corrosion rate (mpy)', tools: ['Coupon analysis data', 'Trending software'], duration: 30, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Perform ultrasonic thickness measurements', expectedResult: 'Identify areas of wall loss', tools: ['UT thickness gauge', 'Grid mapping'], duration: 60, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Analyze produced water chemistry', expectedResult: 'Elevated iron indicates active corrosion', tools: ['Water analysis kit', 'Lab analysis'], duration: 30, skillLevel: 'intermediate' as const },
            ],
            preventiveActions: ['Maintain corrosion inhibitor injection', 'Control water content', 'Use CRAs where needed', 'Implement inspection program'],
            typicalProbability: 0.15,
            affectedComponents: ['Piping', 'Vessels', 'Heat exchangers', 'Valves'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' as const },
            costRange: { min: 5000, max: 500000, currency: 'USD' },
            industryReferences: ['NACE SP0106', 'API RP 571'],
          },
          {
            id: 'h2s_corrosion',
            name: 'H2S Corrosion (Sour Corrosion)',
            description: 'Hydrogen sulfide related corrosion and cracking',
            category: 'corrosion',
            symptoms: ['Sulfide stress cracking', 'Hydrogen induced cracking', 'Corrosion under insulation', 'Elevated H2S in off-gas'],
            diagnosticSteps: [
              { order: 1, action: 'Review material certifications for NACE compliance', expectedResult: 'Verify materials meet MR0175 requirements', tools: ['Material records', 'NACE MR0175'], duration: 60, skillLevel: 'advanced' as const },
              { order: 2, action: 'Perform wet fluorescent magnetic particle testing', expectedResult: 'Detect surface breaking cracks', tools: ['WFMT equipment', 'UV light'], duration: 120, skillLevel: 'advanced' as const },
              { order: 3, action: 'Ultrasonic shear wave inspection for HIC', expectedResult: 'Detect internal hydrogen damage', tools: ['UT shear wave equipment'], duration: 180, skillLevel: 'expert' as const },
            ],
            preventiveActions: ['Use NACE compliant materials', 'Maintain H2S scavenger injection', 'Control operating stresses', 'Implement RBI program'],
            typicalProbability: 0.10,
            affectedComponents: ['Piping', 'Vessels', 'Valves', 'Fittings'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 48, typical: 168, max: 720, unit: 'hours' as const },
            costRange: { min: 25000, max: 1000000, currency: 'USD' },
            industryReferences: ['NACE MR0175/ISO 15156', 'API RP 571', 'NACE SP0296'],
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical Failures',
        description: 'Root causes related to electrical and instrumentation systems',
        icon: 'Zap',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'motor_failure',
            name: 'Electric Motor Failure',
            description: 'Winding insulation breakdown or rotor issues',
            category: 'electrical',
            symptoms: ['Elevated motor temperature', 'Current imbalance', 'Insulation resistance decline', 'Audible humming'],
            diagnosticSteps: [
              { order: 1, action: 'Perform motor circuit analysis (MCA)', expectedResult: 'Check impedance balance and insulation', tools: ['Motor circuit analyzer', 'Megohmmeter'], duration: 30, skillLevel: 'advanced' as const },
              { order: 2, action: 'Measure current on all phases', expectedResult: 'Current imbalance should be <10%', tools: ['Clamp meter', 'Power analyzer'], duration: 15, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Perform insulation resistance test', expectedResult: 'Minimum 1 MΩ per kV + 1 MΩ', tools: ['Megohmmeter'], duration: 20, skillLevel: 'advanced' as const, safetyPrecautions: ['Lockout/Tagout', 'Discharge capacitance'] },
            ],
            preventiveActions: ['Regular motor testing program', 'Ensure proper cooling', 'Protect from voltage transients', 'Maintain clean environment'],
            typicalProbability: 0.12,
            affectedComponents: ['Stator windings', 'Rotor', 'Insulation', 'Bearings'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 16, max: 72, unit: 'hours' as const },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['IEEE 1415', 'NEMA MG1'],
          },
          {
            id: 'instrument_drift',
            name: 'Instrument Calibration Drift',
            description: 'Gradual deviation of transmitter readings from actual values',
            category: 'electrical',
            symptoms: ['Inconsistent readings between similar instruments', 'Process control issues', 'Out-of-spec during calibration check', 'Operator complaints'],
            diagnosticSteps: [
              { order: 1, action: 'Compare reading to reference standard', expectedResult: 'Quantify deviation from standard', tools: ['Calibration standard', 'Reference gauge'], duration: 20, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Check for environmental factors', expectedResult: 'Temperature, vibration, or EMI effects', tools: ['Temperature probe', 'Visual inspection'], duration: 15, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Perform multi-point calibration', expectedResult: 'Verify linearity across range', tools: ['Multi-point calibrator'], duration: 45, skillLevel: 'advanced' as const },
            ],
            preventiveActions: ['Establish calibration schedule', 'Track calibration trends', 'Protect from environmental factors', 'Use redundant measurements'],
            typicalProbability: 0.20,
            affectedComponents: ['Transmitters', 'Sensors', 'Signal conditioning'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 1, typical: 2, max: 4, unit: 'hours' as const },
            costRange: { min: 100, max: 2000, currency: 'USD' },
            industryReferences: ['ISA-TR20.00.01', 'IEC 61511'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'gas_composition_cascade',
        name: 'Gas Composition Change Cascade',
        description: 'Changes in gas composition lead to multiple downstream issues',
        sequence: ['Reservoir composition change', 'Increased CO2/H2S content', 'Accelerated corrosion', 'Hydrate formation at new conditions', 'Equipment damage', 'Production loss'],
        indicators: ['Gas analysis trending', 'Increased inhibitor consumption', 'Corrosion rate increase', 'Hydrate incidents'],
        preventionStrategy: 'Continuous gas composition monitoring with automatic adjustment of treating systems',
      },
      {
        id: 'bearing_cascade',
        name: 'Bearing-Seal-Contamination Cascade',
        description: 'Bearing damage causes seal failure leading to lubricant contamination',
        sequence: ['Initial bearing wear', 'Increased vibration', 'Seal degradation', 'Contaminant ingress', 'Lubricant degradation', 'Accelerated wear', 'Catastrophic failure'],
        indicators: ['Rising vibration trend', 'Oil leakage', 'Temperature increase', 'Debris in lubricant'],
        preventionStrategy: 'Implement vibration monitoring with early intervention thresholds and regular seal inspections',
      },
      {
        id: 'thermal_stress',
        name: 'Thermal Cycling Stress',
        description: 'Repeated temperature cycling causes fatigue cracking',
        sequence: ['Frequent startups/shutdowns', 'Thermal expansion/contraction', 'Stress concentration at discontinuities', 'Crack initiation', 'Crack propagation', 'Component failure'],
        indicators: ['High cycling frequency', 'Temperature differential', 'Crack indications on NDE'],
        preventionStrategy: 'Minimize cycling, implement controlled startup/shutdown procedures, monitor high-stress locations',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'field_operator', name: 'Field Operator', hourlyRate: 45, currency: 'USD', certifications: ['H2S Safety', 'PEC SafeGulf'] },
      { roleId: 'field_technician', name: 'Field Technician', hourlyRate: 55, currency: 'USD', certifications: ['H2S Safety', 'Well Control'] },
      { roleId: 'compressor_technician', name: 'Compressor Technician', hourlyRate: 65, currency: 'USD', certifications: ['Compressor Training', 'OSHA 30'] },
      { roleId: 'instrumentation_technician', name: 'Instrumentation Technician', hourlyRate: 70, currency: 'USD', certifications: ['ISA CCST', 'H2S Safety'] },
      { roleId: 'turbine_specialist', name: 'Turbine Specialist', hourlyRate: 110, currency: 'USD', certifications: ['OEM Certified', 'GE Trained'] },
      { roleId: 'psv_technician', name: 'PSV Technician', hourlyRate: 70, currency: 'USD', certifications: ['NB VR Stamp', 'API 510'] },
      { roleId: 'pipeline_technician', name: 'Pipeline Technician', hourlyRate: 55, currency: 'USD', certifications: ['OQ Certified', 'Pipeline Safety'] },
      { roleId: 'ndt_technician', name: 'NDT Technician', hourlyRate: 75, currency: 'USD', certifications: ['ASNT Level II', 'UT/MT/PT'] },
      { roleId: 'reliability_engineer', name: 'Reliability Engineer', hourlyRate: 95, currency: 'USD', certifications: ['CMRP', 'API 580'] },
      { roleId: 'process_engineer', name: 'Process Engineer', hourlyRate: 90, currency: 'USD', certifications: ['PE License'] },
      { roleId: 'integrity_engineer', name: 'Integrity Engineer', hourlyRate: 100, currency: 'USD', certifications: ['API 570', 'API 653', 'API 510'] },
      { roleId: 'rotating_equipment_engineer', name: 'Rotating Equipment Engineer', hourlyRate: 95, currency: 'USD', certifications: ['API 617', 'API 610'] },
      { roleId: 'oem_engineer', name: 'OEM Field Engineer', hourlyRate: 175, currency: 'USD', certifications: ['Manufacturer Certified'] },
      { roleId: 'api_inspector', name: 'API Inspector', hourlyRate: 95, currency: 'USD', certifications: ['API 510', 'API 570', 'API 653'] },
      { roleId: 'helper', name: 'Helper/Laborer', hourlyRate: 35, currency: 'USD', certifications: ['OSHA 10', 'PEC SafeGulf'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.75,
  },

  // =============================================================================
  // VISUALIZATION
  // =============================================================================
  visualization: {
    primaryColor: '#f59e0b',
    secondaryColor: '#84cc16',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  // =============================================================================
  // NARRATIVE TEMPLATES
  // =============================================================================
  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Equipment Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Based on {dataPoints} sensor readings over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'production_impact',
      name: 'Production Impact Analysis',
      context: 'prediction',
      audience: ['executive', 'manager'],
      template: 'Potential {failure} on {assetType} "{assetName}" could impact {productionVolume} {productionUnit} of {process}. Estimated financial impact: {costImpact}. Confidence: {confidence}%. Recommended action: {recommendedAction}.',
      variables: [
        { name: 'failure', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'productionVolume', type: 'metric' },
        { name: 'productionUnit', type: 'metric' },
        { name: 'process', type: 'terminology' },
        { name: 'costImpact', type: 'currency' },
        { name: 'confidence', type: 'percentage' },
        { name: 'recommendedAction', type: 'metric' },
      ],
    },
    {
      id: 'maintenance_recommendation',
      name: 'Maintenance Recommendation',
      context: 'maintenance',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Based on current condition monitoring data for {assetName}, {maintenanceType} is recommended within {timeframe}. Key indicators: {indicatorList}. Estimated downtime: {estimatedDowntime}. Required parts: {partsList}. Estimated cost: {estimatedCost}.',
      variables: [
        { name: 'assetName', type: 'asset' },
        { name: 'maintenanceType', type: 'terminology' },
        { name: 'timeframe', type: 'time' },
        { name: 'indicatorList', type: 'metric' },
        { name: 'estimatedDowntime', type: 'time' },
        { name: 'partsList', type: 'metric' },
        { name: 'estimatedCost', type: 'currency' },
      ],
    },
    {
      id: 'anomaly_alert',
      name: 'Anomaly Alert',
      context: 'anomaly',
      audience: ['operator', 'technician', 'engineer'],
      template: 'ANOMALY DETECTED on {assetType} "{assetName}": {anomalyType}. Severity: {severity}. Detected at {detectionTime}. {parameterDescription}. Immediate action: {immediateAction}. Similar patterns occurred {historicalOccurrence}.',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'anomalyType', type: 'terminology' },
        { name: 'severity', type: 'metric' },
        { name: 'detectionTime', type: 'time' },
        { name: 'parameterDescription', type: 'metric' },
        { name: 'immediateAction', type: 'metric' },
        { name: 'historicalOccurrence', type: 'metric' },
      ],
    },
    {
      id: 'root_cause_analysis',
      name: 'Root Cause Analysis Summary',
      context: 'prediction',
      audience: ['engineer', 'manager'],
      template: 'Root Cause Analysis for {assetName} {failureMode}: Primary root cause identified as {rootCause} with {confidence}% confidence. Contributing factors: {contributingFactors}. SHAP analysis shows top drivers: {shapFactors}. Recommended corrective actions: {correctiveActions}. Preventive measures: {preventiveMeasures}.',
      variables: [
        { name: 'assetName', type: 'asset' },
        { name: 'failureMode', type: 'terminology' },
        { name: 'rootCause', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'contributingFactors', type: 'metric' },
        { name: 'shapFactors', type: 'metric' },
        { name: 'correctiveActions', type: 'metric' },
        { name: 'preventiveMeasures', type: 'metric' },
      ],
    },
  ],

  // =============================================================================
  // FEATURES
  // =============================================================================
  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  // =============================================================================
  // DEFAULTS
  // =============================================================================
  defaults: {
    timeRange: '7d',
    refreshInterval: 60,
    defaultDashboard: 'command-center',
  },
}
